import type { GenerationStatus } from '../api/types';

/**
 * Turns the suggestion generator's status into one banner. The server's
 * `message` is shown as-is — it already says what happened in plain words.
 */
export type GenerationViewKind = 'none' | 'running' | 'created' | 'empty' | 'failed' | 'interrupted';

export interface GenerationView {
  kind: GenerationViewKind;
  tone: 'info' | 'success' | 'warning' | 'error';
  message: string;
  progress: { done: number; total: number; current: string | null; pct: number } | null;
  skipped: { key: string; label: string; reason: string | null }[];
}

const NONE: GenerationView = { kind: 'none', tone: 'info', message: '', progress: null, skipped: [] };

export const INTERRUPTED_MESSAGE =
  'The last run was interrupted by a server restart before it finished. Nothing was lost — run it again.';

/** Codes that mean the run couldn't look, rather than looked and found nothing. */
export function isBlockingEmptyCode(code: string | null | undefined): boolean {
  return !!code && (code.startsWith('OUTREACH_') || code === 'TIKTOK_UNAVAILABLE');
}

function isRunning(run: GenerationStatus): boolean {
  return run.outcome ? run.outcome === 'running' : run.running;
}

/** Outcome, derived for servers that don't send it yet. */
function outcomeOf(run: GenerationStatus): NonNullable<GenerationStatus['outcome']> {
  if (run.outcome) return run.outcome;
  if (run.running) return 'running';
  if (run.error) return 'failed';
  if (run.result) return run.result.created > 0 ? 'created' : 'empty';
  return 'idle';
}

function skippedOf(run: GenerationStatus): GenerationView['skipped'] {
  return (run.result?.sourcesSkipped ?? []).map((s, i) => ({
    key: `${s.code ?? ''}:${s.source ?? s.label ?? i}`,
    label: s.label ?? s.source ?? s.code ?? 'Source',
    reason: s.reason ?? (s.label || s.source ? s.code ?? null : null),
  }));
}

/**
 * @param seenRunningStartedAt `startedAt` of a run this page saw in progress.
 *   If the server has since restarted (its `serverStartedAt` is later) and now
 *   reports idle, that run died with the process.
 */
export function generationView(
  run: GenerationStatus | null | undefined,
  seenRunningStartedAt: string | null,
): GenerationView {
  if (!run) return NONE;
  const outcome = outcomeOf(run);

  if (outcome === 'running' || isRunning(run)) {
    const p = run.progress;
    const progress =
      p && p.sourcesTotal > 0
        ? {
            done: p.sourcesDone,
            total: p.sourcesTotal,
            current: p.current ?? null,
            pct: Math.min(100, Math.round((p.sourcesDone / p.sourcesTotal) * 100)),
          }
        : null;
    return {
      kind: 'running',
      tone: 'info',
      message: run.message || 'Generating… this takes a few minutes. Terms appear below as each source finishes.',
      progress,
      skipped: [],
    };
  }

  if (outcome === 'idle') {
    const seen = seenRunningStartedAt ? Date.parse(seenRunningStartedAt) : NaN;
    const restarted = run.serverStartedAt ? Date.parse(run.serverStartedAt) : NaN;
    if (Number.isFinite(seen) && Number.isFinite(restarted) && restarted > seen) {
      return { kind: 'interrupted', tone: 'warning', message: INTERRUPTED_MESSAGE, progress: null, skipped: [] };
    }
    return NONE;
  }

  if (outcome === 'failed') {
    return {
      kind: 'failed',
      tone: 'error',
      message: run.message || (run.error ? `Last run failed: ${run.error}` : 'Last run failed.'),
      progress: null,
      skipped: skippedOf(run),
    };
  }

  if (outcome === 'empty') {
    const code = run.result?.emptyReasonCode ?? null;
    return {
      kind: 'empty',
      tone: isBlockingEmptyCode(code) ? 'error' : 'warning',
      message: run.message || run.result?.emptyReason || 'The last run found no new terms.',
      progress: null,
      skipped: skippedOf(run),
    };
  }

  const r = run.result;
  return {
    kind: 'created',
    tone: 'success',
    message:
      run.message ||
      (r ? `Last run: ${r.created} new ${r.created === 1 ? 'term' : 'terms'} from ${r.candidatesFound} candidates.` : 'Last run finished.'),
    progress: null,
    skipped: skippedOf(run),
  };
}
